"use client";

import React, { useMemo, useState, useCallback } from "react";
import { MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import {
  useTaskNotes,
  useDeleteTaskNote,
  type TaskNote,
} from "@/lib/hooks/api/tasks";
import { CreateTaskNoteForm } from "@/features/tasks/components/components/CreateTaskNoteForm";
import { NoteCommentRow } from "./NoteCommentRow";
import type { EntityNoteBase } from "./types";

function toEntityNote(note: TaskNote): EntityNoteBase {
  return {
    id: note.id,
    content: note.content,
    created_at: note.created_at,
    updated_at: note.updated_at,
    note_type: note.note_type,
    is_internal: note.is_internal,
    user: note.user
      ? {
          id: note.user.id,
          first_name: note.user.first_name,
          last_name: note.user.last_name,
          email: note.user.email,
        }
      : null,
  };
}

export interface TaskNotesTimelineProps {
  taskId: string;
  /** Max height of scrollable list */
  maxHeight?: string;
  className?: string;
}

/**
 * Chronological task notes with edit/delete — uses shared NoteCommentRow for reuse on other entities later.
 */
export function TaskNotesTimeline({ taskId, maxHeight = "280px", className }: TaskNotesTimelineProps) {
  const { data: notes, isLoading, error } = useTaskNotes(taskId);
  const deleteMutation = useDeleteTaskNote();
  const [editing, setEditing] = useState<TaskNote | null>(null);
  const [minimized, setMinimized] = useState(false);

  const sorted = useMemo(() => {
    const list = notes ?? [];
    return [...list].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }, [notes]);

  const handleDelete = useCallback(
    async (noteId: string) => {
      if (!window.confirm("Delete this note?")) return;
      await deleteMutation.mutateAsync(noteId);
    },
    [deleteMutation]
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        Loading comments…
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-sm text-destructive">
        Could not load comments.
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Comments</h3>
          <span className="text-xs text-muted-foreground">({sorted.length})</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => setMinimized((prev) => !prev)}
        >
          {minimized ? (
            <>
              <ChevronDown className="h-3.5 w-3.5 mr-1" />
              Expand
            </>
          ) : (
            <>
              <ChevronUp className="h-3.5 w-3.5 mr-1" />
              Minimize
            </>
          )}
        </Button>
      </div>

      {minimized ? null : sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">No comments yet.</p>
      ) : (
        <div
          className="space-y-2 overflow-y-auto pr-1"
          style={{ maxHeight }}
        >
          {sorted.map((note) => (
            <NoteCommentRow
              key={note.id}
              note={toEntityNote(note)}
              // validate-memo-stable-props-allow: per-item handler in list render
              onEdit={() => setEditing(note)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <ResponsiveModal
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
        title="Edit note"
        maxWidth="md"
      >
        {editing ? (
          <CreateTaskNoteForm
            taskId={taskId}
            noteId={editing.id}
            initialValues={{
              content: editing.content,
              note_type: editing.note_type,
              is_internal: editing.is_internal,
            }}
            onSuccess={() => setEditing(null)}
            onCancel={() => setEditing(null)}
          />
        ) : null}
      </ResponsiveModal>
    </div>
  );
}
